import React, { useEffect, useRef } from "react";
import { Container, Col, Row } from "react-bootstrap";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ListCard from "../ListCard";
import SpinWheel from "../SpinWheel/SpinWheel";
import LotteryCard2 from "../mine-card/MineCard";
import GameArenaCard from "./GameArenaCard";

gsap.registerPlugin(ScrollTrigger);

const GameArenaDesktop = (props) => {
  const {
    lotterylist1, lotterylist2, lotterylist3, lotterylist4,
    partnerlists1, partnerlists2, partnerlists3, partnerlists4,
    partnerLogoData, history, triggerRef
  } = props;

  const deckRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cardElements = gsap.utils.toArray(".arena-card", deckRef.current);
      if (cardElements.length > 0) {
        gsap.from(cardElements, {
          y: "+=160",
          opacity: 0,
          duration: 0.8,
          stagger: 0.05,
          ease: "power3.out",
          scrollTrigger: {
            trigger: triggerRef ? triggerRef.current : deckRef.current,
            start: "top 65%",
            toggleActions: "play none none reverse"
          }
        });
      }
    }, deckRef);
    return () => ctx.revert();
  }, [triggerRef]);

  const findPartnerByAddress = (address, list) => {
    if (!list) return null;
    return list.find((p) => p.walletAddress === address);
  };

  const cards = [
    {
      id: "missile",
      accentColor: "#5b8dd9",
      label: "Autobet Missile",
      content: lotterylist4?.length ? (
        <Row className="home-grid lottery-section2 text-center" style={{ width: "100%", margin: 0 }}>
          {lotterylist4.slice(0, 3).map((item, index) => (
            <Col lg={4} key={`${item.lotteryId || index}-mis-d`}>
              <LotteryCard2
                history={history}
                item={item}
                lotteryType="missile"
                partner={findPartnerByAddress(item.partnerAddress, partnerlists4)}
                partnerLogo={partnerLogoData}
              />
            </Col>
          ))}
        </Row>
      ) : null
    },
    {
      id: "mine",
      accentColor: "#3dbfa8",
      label: "Autobet Mine",
      content: lotterylist3?.length ? (
        <Row className="home-grid lottery-section2 text-center" style={{ width: "100%", margin: 0 }}>
          {lotterylist3.slice(0, 3).map((item, index) => (
            <Col lg={4} key={`${item.lotteryId || index}-mine-d`}>
              <LotteryCard2
                history={history}
                item={item}
                partner={findPartnerByAddress(item.partnerAddress, partnerlists3)}
                partnerLogo={partnerLogoData}
              />
            </Col>
          ))}
        </Row>
      ) : null
    },
    {
      id: "revolver",
      accentColor: "#fc9d2e",
      label: "Autobet Revolver",
      content: lotterylist1?.length ? (
        <Row className="home-grid lottery-section2 text-center" style={{ width: "100%", margin: 0 }}>
          {lotterylist1.slice(0, 3).map((item, index) => (
            <Col lg={4} key={`${item.lotteryId || index}-rev-d`}>
              <SpinWheel 
                history={history} 
                lotteryData={item} 
                partner={findPartnerByAddress(item.partnerAddress, partnerlists1)}
                partnerLogo={partnerLogoData}
              />
            </Col>
          ))}
        </Row>
      ) : null
    },
    {
      id: "mrl",
      accentColor: "#ea2e49",
      label: "Autobet MRL",
      content: lotterylist2?.length ? (
        <Row className="home-grid lottery-section2 text-center" style={{ width: "100%", margin: 0 }}>
          {lotterylist2.slice(0, 3).map((item, index) => (
            <Col lg={4} key={`${item.lotteryId || index}-mrl-d`}>
              <ListCard
                history={history}
                item={item}
                partner={findPartnerByAddress(item.partnerAddress, partnerlists2)}
                partnerLogo={partnerLogoData}
              />
            </Col>
          ))}
        </Row>
      ) : null
    }
  ];

  return (
    <div className="arena-deck-wrapper" ref={deckRef}>
      <div className="arena-deck">
        {[...cards].reverse().map((card, revIdx) => {
          const idx = cards.length - 1 - revIdx;
          return (
            <GameArenaCard
              key={card.id}
              id={card.id}
              idx={idx}
              accentColor={card.accentColor}
              label={card.label}
              content={card.content}
            />
          );
        })}
      </div>
    </div>
  );
};

const GameArenaConnectCTA = ({ onConnect }) => {
  return (
    <div className="arena-connect-wrapper">
      <div className="arena-connect-inner">
        <div className="arena-connect-icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="14" fill="rgba(252,157,46,0.12)" />
            <path d="M14 20h20M14 24h12M14 28h8" stroke="#fc9d2e" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="35" cy="26" r="7" fill="#0f2a32" />
            <path d="M33 26h4M35 24v4" stroke="#fc9d2e" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h3 className="arena-connect-title">Connect your wallet to play</h3>
        <p className="arena-connect-copy">
          Access live AutoBET games — MRL draws, Revolver spins, Mines, Missiles,
          and Gift Cards — after connecting your wallet.
        </p>
        <button className="arena-connect-btn" onClick={onConnect}>
          Connect Wallet
        </button>
      </div>
    </div>
  );
};

const GameArenaSection = (props) => {
  const sectionRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. Text Reveal Entry
      if (introRef.current && introRef.current.children) {
        gsap.fromTo(
          introRef.current.children,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            stagger: 0.15,
            ease: "power2.out",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 65%",
              toggleActions: "play none none reverse"
            }
          }
        );
      }

      // 2. Pinned Scrub Timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 60px", // Pin slightly down to compensate for navbar
          end: "+=300%", 
          scrub: 1,
          pin: true,
          pinSpacing: true,
        }
      });

      // Initial pause so the user can scroll a bit while observing the fully loaded static view
      tl.to({}, { duration: 0.5 });

      const cards = gsap.utils.toArray(".arena-card", sectionRef.current).reverse();

      if (cards.length > 0) {
        const backCard = cards[cards.length - 1]; // Red (z-index 2)
        const frontCards = cards.slice(0, cards.length - 1); // Blue, Green, Orange
        
        // Step 1: Title shrinks, Back Card (Red) grows up from behind, remaining cards push down
        if (introRef.current) {
          tl.to(introRef.current, { scale: 0.9, opacity: 0, duration: 1, ease: "power2.inOut" }, "step1");
        }

        tl.to(backCard, { height: "90vh", y: 0, rotate: 0, duration: 2, ease: "power2.inOut" }, "step1");

        if (frontCards.length > 0) {
          tl.to(frontCards, { scale: 0.9, y: "+=30", duration: 2, ease: "power2.inOut" }, "step1");
        }

        // Step 2, 3, 4: Sequentially grow the remaining cards from back to front
        let stepNum = 2;
        for (let i = cards.length - 2; i >= 0; i--) {
          tl.to(cards[i], { height: "90vh", y: 0, rotate: 0, scale: 1, duration: 2, ease: "power2.inOut" }, `step${stepNum}`);
          stepNum++;
        }
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [props.walletAddress]);

  return (
    <section id="game-arena" className="home-arena" ref={sectionRef}>
      <Container className="home-container">
        <div className="home-arena-intro" ref={introRef}>
          <p className="home-kicker">Game arena</p>
          <h2 className="home-section-title">Choose your game</h2>
          <p className="home-section-copy">
            Jump into live AutoBET modes — classic draws, spin wheels, mines,
            missiles, and gift cards built for on-chain fundraising.
          </p>
        </div>
      </Container>

      {props.walletAddress ? (
        <GameArenaDesktop {...props} triggerRef={sectionRef} />
      ) : (
        <GameArenaConnectCTA onConnect={props.onConnect} />
      )}
    </section>
  );
};

export default GameArenaSection;
